import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCircle2, XCircle } from 'lucide-react'
import clsx from 'clsx'
import { useNotificationsStore } from '@/store/notifications'

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'ahora mismo'
  if (mins < 60) return `hace ${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `hace ${hours} h`
  const days = Math.floor(hours / 24)
  return `hace ${days} d`
}

interface DropdownPos {
  top: number
  left: number
}

export default function NotificationBell() {
  const navigate = useNavigate()
  const { unreadCount, items, load, markRead } = useNotificationsStore()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<DropdownPos>({ top: 0, left: 0 })
  const bellRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const loadNotifications = useCallback(() => {
    load()
  }, [load])

  useEffect(() => {
    loadNotifications()
    const interval = setInterval(loadNotifications, 60000)
    return () => clearInterval(interval)
  }, [loadNotifications])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleDropdown = () => {
    if (open) {
      setOpen(false)
      return
    }
    const rect = bellRef.current?.getBoundingClientRect()
    if (!rect) {
      setOpen(true)
      return
    }
    const width = Math.min(384, window.innerWidth - 16)
    let left = rect.left
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8)
    }
    let top = rect.bottom + 8
    if (top + 420 > window.innerHeight) {
      top = Math.max(8, rect.top - 420 - 8)
    }
    setPos({ top, left })
    setOpen(true)
  }

  const handleItemClick = (id: number) => {
    markRead(id)
    navigate(`/notifications?event=${id}`)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={bellRef}
        onClick={toggleDropdown}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-[100] w-80 md:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Notificaciones
            </h3>
            <button
              onClick={() => { navigate('/notifications'); setOpen(false) }}
              className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
            >
              Ver todas
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-400">
                No hay notificaciones todavía.
              </div>
            )}
            {items.slice(0, 10).map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={clsx(
                  'w-full text-left px-4 py-3 flex items-start gap-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors',
                  !item.read && 'bg-orange-50/50 dark:bg-orange-900/10'
                )}
              >
                {item.type === 'backfill_failed' ? (
                  <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={clsx(
                      'text-sm font-medium truncate',
                      item.read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'
                    )}>
                      {item.title}
                    </p>
                    {!item.read && <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                    {item.body}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {timeAgo(item.createdAt)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
